import { getApi, postApi } from './baseApi';
import { NOTIFICATION_ENDPOINTS } from '../constants/endpoints';

// The panel's notifications (admin-backend app/routers/panel_notifications.py): the owner and every
// staff user, each seeing the events of their roles.
export type NotificationEvent =
  | 'partner_registered'
  | 'partner_recharge_paid'
  | 'upload_request'
  | 'client_payment'
  | 'client_registered'
  | 'kyc_submitted'
  | 'location_request'
  | 'channel_applied'
  | 'material_order_paid';

export interface PanelNotification {
  id: number;
  event: NotificationEvent;
  title: string;
  body: string;
  // The panel page it opens, e.g. /dashboard/partners/<id>
  link: string;
  occurred_at: string;
  unread: boolean;
}

export interface NotificationPage {
  items: PanelNotification[];
  total: number;
  unread_count: number;
  // Pass as before_id for the next page; null on the last one
  next_before_id: number | null;
}

export interface NotificationListParams {
  limit?: number;
  before_id?: number;
  event?: NotificationEvent;
  unread?: boolean;
}

export interface PushConfig {
  // False while the server sends no pushes: hide the "turn on" button
  enabled: boolean;
  public_key: string | null;
}

export const getNotificationsApi = (params: NotificationListParams = {}): Promise<NotificationPage> =>
  getApi(NOTIFICATION_ENDPOINTS.LIST, {
    ...(params.limit && { limit: params.limit }),
    ...(params.before_id && { before_id: params.before_id }),
    ...(params.event && { event: params.event }),
    ...(params.unread && { unread: true }),
  });

export const getUnreadCountApi = (): Promise<{ unread_count: number }> => getApi(NOTIFICATION_ENDPOINTS.UNREAD_COUNT);

export const markNotificationReadApi = (id: number): Promise<any> => postApi(NOTIFICATION_ENDPOINTS.READ(id));

export const markAllNotificationsReadApi = (): Promise<any> => postApi(NOTIFICATION_ENDPOINTS.READ_ALL);

export const getPushConfigApi = (): Promise<PushConfig> => getApi(NOTIFICATION_ENDPOINTS.PUSH);

// The browser's PushSubscription.toJSON(): { endpoint, keys: { p256dh, auth } }
export const subscribePushApi = (subscription: PushSubscriptionJSON): Promise<any> =>
  postApi(NOTIFICATION_ENDPOINTS.PUSH_SUBSCRIBE, subscription);

export const unsubscribePushApi = (endpoint: string): Promise<any> =>
  postApi(NOTIFICATION_ENDPOINTS.PUSH_UNSUBSCRIBE, { endpoint });

export const sendTestPushApi = (): Promise<{ sent: number; subscriptions: number }> =>
  postApi(NOTIFICATION_ENDPOINTS.PUSH_TEST);

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  partner_registered: 'New partner',
  partner_recharge_paid: 'Partner wallet payment',
  upload_request: 'Upload request',
  client_payment: 'Client payment',
  client_registered: 'New client',
  kyc_submitted: 'KYC submitted',
  location_request: 'Location request',
  channel_applied: 'Channel application',
  material_order_paid: 'Material order paid',
};

// [icon, theme colour] per event
export const NOTIFICATION_EVENT_ICONS: Record<NotificationEvent, [string, string]> = {
  partner_registered: ['eva:person-add-fill', 'primary.main'],
  partner_recharge_paid: ['eva:credit-card-fill', 'success.main'],
  upload_request: ['eva:cloud-upload-fill', 'info.main'],
  client_payment: ['eva:credit-card-fill', 'success.main'],
  client_registered: ['eva:person-done-fill', 'primary.main'],
  kyc_submitted: ['eva:shield-fill', 'warning.main'],
  location_request: ['eva:pin-fill', 'warning.main'],
  channel_applied: ['eva:layers-fill', 'info.main'],
  material_order_paid: ['eva:shopping-bag-fill', 'success.main'],
};
